import { FC, useMemo, useState } from "react";
import { Box, Container, Grid, GridItem, VBox } from "../../components-v2/Box";
import { Button, IconButton } from "../../components-v2/Button";
import { useLanguage, useTranslation } from "../../stores/RootStoreContext";
import { isMobileView } from "../../utils/screen";
import { HiddenForMobile } from "../../components-v2/Mobile";
import Laptop from "../../assets/img/dcompany_laptop.png";
import { AutoImportSectionProps, isNotRegisteredIssue } from "./transactionLogDomain";
import { isCountryCodeNorway } from "../../utils/countries";
import { Dialog } from "../../components-v2/Dialog";
import { isFailure, isPending, Result } from "../../common/common";
import {
  canDeleteTransaction,
  maxDateForTransaction,
  minDateForTransaction,
} from "../../types/const/Transactions";
import { Alert } from "../../components-v2/Alert";
import { observer } from "mobx-react";
import { IssueNewSharesDTO, ShareholdingPerDateDTO, Transaction } from "../../types/Transactions";
import ShareholdersStore from "../../stores/ShareholdersStore";
import { TMessage } from "../../i18n/tt";
import { sortBy } from "lodash";
import { TransactionDateAndTimeForm } from "./commonTransactionsDomain";
import { toIsoString } from "../../utils/Dates";
import { formatDateTime } from "../../i18n/dateFormat";
import { TransactionDateAndTime } from "./commonTransactionComponents";
import { NumberFieldModel } from "../../common/commonForms";
import { MTextInput } from "../../components-v2/TextInput";
import { Markdown } from "../../text/Markdown";

export const AutoImportSection: FC<{
  props: AutoImportSectionProps;
}> = ({ props }) => {
  const { t } = useTranslation();
  const norwegianCompany = isCountryCodeNorway(props.country);
  return (
    <Container border className="bg-default">
      <VBox padding>
        <Box justifyContent={"end"}>
          <IconButton
            icon={{ name: "x", color: "default", size: "lg" }}
            tooltip={t("close")}
            onClick={props.onConfirmAutoImportIsRead}
          />
        </Box>
        <Box>
          <Grid cols={3}>
            <GridItem colSpan={isMobileView() ? 3 : 2}>
              <VBox>
                <h3>
                  {norwegianCompany
                    ? t("transactions.only-autoimport.header", {
                        onboardingDate: props.onboardingDate,
                      })
                    : t("transactions.only-autoimport-foreign.header", {
                        onboardingDate: props.onboardingDate,
                      })}
                </h3>
                <div>
                  {norwegianCompany
                    ? t("transactions.only-autoimport.p1")
                    : t("transactions.only-autoimport-foreign.p1")}
                </div>
                <div>{t("transactions.only-autoimport.p2")}</div>
                <div>{t("transactions.only-autoimport.p3")}</div>
                <Box paddingTop={"lg"}>
                  <Button
                    icon={{ name: "thumbs-up", color: "accent3", weight: "fill" }}
                    label={t("ok")}
                    variant="secondary"
                    onClick={props.onConfirmAutoImportIsRead}
                    loading={props.isLoading}
                  />
                </Box>
              </VBox>
            </GridItem>
            <HiddenForMobile>
              <GridItem>
                <Box>
                  <img src={Laptop} alt="dCompany" />
                </Box>
              </GridItem>
            </HiddenForMobile>
          </Grid>
        </Box>
      </VBox>
    </Container>
  );
};

export const ChangeTransactionDateDialog: FC<{
  selectedTransaction: Transaction;
  onSubmit: (transaction: Transaction, date: string) => void;
  onCancel: () => void;
  submitResult?: Result<TMessage, TMessage>;
  transactions: Transaction[];
  infoText?: TMessage;
}> = observer(
  ({ selectedTransaction, onSubmit, onCancel, submitResult, transactions, infoText }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const sortedTransactions = sortBy(transactions, "transactionDate");
    const minDate = minDateForTransaction(selectedTransaction, sortedTransactions);
    const maxDate = maxDateForTransaction(selectedTransaction, sortedTransactions);
    const [form] = useState(() => {
      return new TransactionDateAndTimeForm(
        minDate,
        { value: selectedTransaction.transactionDate },
        maxDate,
      );
    });
    const [errorMessage, setErrorMessage] = useState<TMessage | null>(null);

    return (
      <Dialog
        title={t(
          isNotRegisteredIssue(selectedTransaction)
            ? "transactions.registration-date"
            : "transactions.change-date-for",
          {
            transactionType: t(selectedTransaction.transactionType).toLowerCase(),
          },
        )}
        maxWidth={"sm"}
        showAlert={false}
        onSubmit={() => {
          setErrorMessage(null);
          if (!form.validate()) return;
          if (
            transactions.some((t) => toIsoString(t.transactionDate) === form.combinedDateAndTime)
          ) {
            setErrorMessage({ key: "transactions.change-date.error-same-date" });
            return;
          }
          onSubmit(selectedTransaction, form.combinedDateAndTime!);
        }}
        onCancel={onCancel}
        isSubmitDisabled={!form.isSubmitEnabled}
        submitResult={submitResult}
        isSubmitting={isPending(submitResult)}
      >
        <VBox>
          {maxDate && (
            <p>
              {t("transactions.change-date.initial-transaction", {
                maxDate: formatDateTime(locale, maxDate),
              })}
            </p>
          )}
          {minDate && (
            <p>
              {t("transactions.change-date.other-transaction", {
                minDate: formatDateTime(locale, minDate),
              })}
            </p>
          )}
          {infoText && <Markdown value={t(infoText)} />}
          <TransactionDateAndTime form={form} />
          {errorMessage && (
            <Alert message={<Markdown value={t(errorMessage)} />} type={"warning"} />
          )}
          {isFailure(submitResult) && (
            <Alert
              message={<Markdown value={t(submitResult.error)} />}
              type={"warning"}
              paddingY={"sm"}
              marginY={"sm"}
            />
          )}
        </VBox>
      </Dialog>
    );
  },
);

const ChangeNominalValueDialog: FC<{
  selectedTransaction: Transaction;
  onSubmit: (transaction: Transaction, nominalValue: number) => void;
  onCancel: () => void;
  submitResult?: Result<TMessage, TMessage>;
}> = observer(({ selectedTransaction, onSubmit, onCancel, submitResult }) => {
  const { t } = useTranslation();
  const shareNominalValue =
    selectedTransaction.transactionType === "SHAREHOLDING_PER_DATE"
      ? (selectedTransaction.payload as ShareholdingPerDateDTO["data"]).nominalValue
      : (selectedTransaction.payload as IssueNewSharesDTO["data"]).shareNominalValue;
  const input = useMemo(
    () => new NumberFieldModel({ isPositive: true, required: true, value: shareNominalValue }),
    [shareNominalValue],
  );

  return (
    <Dialog
      title={t("transactions.change-nominal-value", {
        transactionType: t(selectedTransaction.transactionType).toLowerCase(),
      })}
      maxWidth={"xs"}
      showAlert={false}
      onSubmit={() => {
        const value = input.parsedValue;
        if (!input.validate() || !value) return;
        onSubmit(selectedTransaction, value);
      }}
      onCancel={onCancel}
      isSubmitDisabled={input.isEmpty || !input.isValid}
      submitResult={submitResult}
      isSubmitting={isPending(submitResult)}
    >
      <VBox>
        <MTextInput model={input} label={t("transactions.new-nominal")} />
      </VBox>
    </Dialog>
  );
});

const DeleteTransactionDialog = observer(
  ({
    submitResult,
    onSubmit,
    selectedTransaction,
    onCancel,
    transactionLog,
  }: {
    selectedTransaction: Transaction;
    onSubmit: (transaction: Transaction) => void;
    onCancel: () => void;
    submitResult?: Result<TMessage, TMessage>;
    transactionLog: Transaction[];
  }) => {
    const { t } = useTranslation();

    return (
      <Dialog
        title={
          t("transactions.delete") + " " + t(selectedTransaction.transactionType).toLowerCase()
        }
        maxWidth={"sm"}
        showAlert={false}
        submitResult={submitResult}
        onCancel={onCancel}
        actions={
          <div
            className={
              "tw-flex tw-flex-col tw-gap-2 tw-justify-end md:tw-flex-row-reverse md:tw-justify-start"
            }
          >
            {!isFailure(submitResult) &&
              canDeleteTransaction(selectedTransaction, transactionLog.length) && (
                <Button
                  variant={"primary"}
                  label={
                    t("transactions.delete") +
                    " " +
                    t(selectedTransaction.transactionType).toLowerCase()
                  }
                  loading={isPending(submitResult)}
                  onClick={() => onSubmit(selectedTransaction!)}
                  disabled={!canDeleteTransaction(selectedTransaction, transactionLog.length)}
                />
              )}
            <Button
              variant={"secondary"}
              label={
                !isFailure(submitResult) &&
                canDeleteTransaction(selectedTransaction, transactionLog.length)
                  ? t("cancel")
                  : t("close")
              }
              onClick={onCancel}
              disabled={isPending(submitResult)}
            />
          </div>
        }
      >
        {canDeleteTransaction(selectedTransaction, transactionLog.length) ? (
          submitResult === undefined && <Box>{t("transactions.delete.warning")}</Box>
        ) : (
          <Box>{t("transactions.delete.disabled")}</Box>
        )}

        {isFailure(submitResult) && (
          <VBox>
            <Alert message={t("transactions.delete.error")} type={"error"} icon={false} />
            <p>{t(submitResult.error)}</p>
          </VBox>
        )}
      </Dialog>
    );
  },
);

export const TransactionDialogs = observer(
  ({ shareholdersStore }: { shareholdersStore: ShareholdersStore }) => {
    const {
      changeNominalValueResult,
      doChangeNominalValue,
      deleteResult,
      doDeleteTransaction,
      selectedTransaction,
      showChangeNominalValueDialog,
      showDeleteTransactionDialog,
      transactionLog,
      cancelChangeNominalValue,
      cancelDeleteTransaction,
    } = shareholdersStore;

    if (!selectedTransaction) {
      return null;
    }

    return (
      <>
        {showChangeNominalValueDialog && (
          <ChangeNominalValueDialog
            selectedTransaction={selectedTransaction}
            onSubmit={doChangeNominalValue}
            onCancel={cancelChangeNominalValue}
            submitResult={changeNominalValueResult}
          />
        )}
        {showDeleteTransactionDialog && (
          <DeleteTransactionDialog
            selectedTransaction={selectedTransaction}
            onSubmit={doDeleteTransaction}
            onCancel={cancelDeleteTransaction}
            submitResult={deleteResult}
            transactionLog={transactionLog}
          />
        )}
      </>
    );
  },
);
